import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import * as dayjs from 'dayjs';
import * as dayjsTimezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import { Subscription } from 'rxjs';
import { IntegrationService } from 'src/app/services/integration.service';
import { PBXService } from 'src/app/services/pbx.service';

import { CallLogFilters } from '../../models/call-history.model';
import {
  defaultUsedFilters,
  FilterBoolean,
  filters,
  FilterType,
  IDateTime,
  IFilter,
} from '../../models/call-history-filter.models';

@Component({
  selector: 'app-call-history-filters',
  templateUrl: './call-history-filters.component.html',
  styleUrls: ['./call-history-filters.component.scss'],
})
export class CallHistoryFiltersComponent implements OnInit, OnDestroy {
  @Input() public set closePopup(value: { isClosed?: boolean; reason?: 'close' | 'apply' }) {
    if (value.isClosed) return;

    if (value.reason === 'close') {
      this.reinitForm();
    }
  }

  @Output() public selectedFilters: EventEmitter<CallLogFilters> = new EventEmitter();
  public usedFilters: IFilter[] = [...defaultUsedFilters];
  public unusedFilters: IFilter[] = [];
  public lastUsedFilters: IFilter[] = [...defaultUsedFilters];
  public FilterType = FilterType;
  public form: FormGroup = new FormGroup({});
  private lastFormValues: Record<string, any> = {};
  private timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'Europe/Rome';
  private aiAgentSub: Subscription | null = null;

  constructor(
    private pbxService: PBXService,
    private integrationService: IntegrationService
  ) {
    const pbx = this.pbxService.getCachedPbx();
    if (pbx) {
      this.timeZone = pbx.time_zone;
    }
  }

  private get availableFilters(): IFilter[] {
    return filters.filter((f) => f.name !== 'is_ai_agent' || this.integrationService.hasAiAgent);
  }

  ngOnInit(): void {
    this.unusedFilters = [...this.availableFilters];
    this.initForm();
    this.aiAgentSub = this.integrationService.hasAiAgent$.subscribe(() => {
      if (!this.integrationService.hasAiAgent) {
        const aiAgentFilter = this.usedFilters.find((f) => f.name === 'is_ai_agent');
        this.usedFilters = this.usedFilters.filter((f) => f.name !== 'is_ai_agent');
        this.lastUsedFilters = this.lastUsedFilters.filter((f) => f.name !== 'is_ai_agent');
        delete this.lastFormValues['is_ai_agent'];
        if (aiAgentFilter) this.removeFormControl(aiAgentFilter);
      }
      this.unusedFilters = this.availableFilters.filter((f) => !this.usedFilters.includes(f));
    });
  }

  ngOnDestroy(): void {
    this.aiAgentSub?.unsubscribe();
  }

  public addFilter(filter: IFilter, value?: any): void {
    switch (filter.type) {
      case FilterType.DATE_TIME:
        this.addFormControl(filter, value || filter.default);
        break;
      case FilterType.TIMEZONE:
        this.addFormControl(filter, value || this.timeZone);
        break;
      default:
        this.addFormControl(filter, value);
    }
  }

  public swapFilter(oldFilter: IFilter, newFilter: IFilter): void {
    this.usedFilters = this.usedFilters.map((f) => (f === oldFilter ? newFilter : f));
    this.unusedFilters = this.unusedFilters.map((f) => (f === newFilter ? oldFilter : f));
    this.removeFormControl(oldFilter);
    this.addFormControl(newFilter);
  }

  public addFirstFilter(): void {
    const filter = this.unusedFilters[0];
    this.usedFilters = [...this.usedFilters, this.unusedFilters[0]];
    this.unusedFilters = this.unusedFilters.slice(1);
    this.addFilter(filter);
  }

  public removeFilter(filter: IFilter | null | undefined): void {
    if (!filter) return;

    this.usedFilters = this.usedFilters.filter((f) => f !== filter);
    if (this.availableFilters.some((f) => f.name === filter.name)) {
      this.unusedFilters = [...this.unusedFilters, filter];
    }
    this.removeFormControl(filter);
  }

  public formSubmit(): void {
    let dtrOutput: IDateTime[] = [];
    if (this.form.value.date_time_ranges) {
      dtrOutput = this.convertDateTimeRange(this.form.value.date_time_ranges);
    }
    const output: CallLogFilters = {
      account_id: null,
      ...this.form.value,
      date_time_ranges: dtrOutput,
    };
    Object.keys(output).forEach((k: string) => {
      if (output[k] == FilterBoolean.yes || output[k] == FilterBoolean.no) {
        output[k] = output[k] == FilterBoolean.yes ? true : false;
      }
      if (output[k] === null || output[k] === undefined || output[k].length === 0 || k === 'timezone') delete output[k];
    });
    this.selectedFilters.emit(output);

    Object.keys(this.form.value).forEach((f) => {
      const c = this.form.get(f);
      if (c && c.value === null) {
        const filterToRemove = this.usedFilters.find((usedF) => usedF.name === f);
        this.removeFilter(filterToRemove);
      }
    });

    this.saveFormData();
  }

  public formClear(): void {
    this.form = new UntypedFormGroup({});
    this.usedFilters = [...defaultUsedFilters];
    this.unusedFilters = [...this.availableFilters];
    this.initForm();
  }

  private convertDateTimeRange(dateTimeRanges: any): IDateTime[] {
    const dtrOutput: IDateTime[] = [];
    dayjs.extend(dayjsTimezone);
    dayjs.extend(utc);
    dateTimeRanges.forEach((dtr: IDateTime) => {
      const dtrTemp: IDateTime = {
        from: '',
        to: '',
        from_time: '',
        to_time: '',
      };
      dtrTemp.from = `${dayjs.tz(dtr.from, this.form.value.timezone).format()}[${this.form.value.timezone}]`;
      dtrTemp.to = `${dayjs.tz(dtr.to, this.form.value.timezone).format()}[${this.form.value.timezone}]`;
      dtrTemp.from_time = dtr.from_time;
      dtrTemp.to_time = dtr.to_time;
      dtrOutput.push(dtrTemp);
    });

    return dtrOutput;
  }

  private addFormControl(filter: IFilter, value?: any): void {
    if (filter.type === FilterType.DROPDOWN) {
      this.form.addControl(filter.name, new FormControl<any>(value || filter.options[0]));
    } else {
      this.form.addControl(filter.name, new FormControl<any>(value));
    }
  }

  private initForm(): void {
    this.usedFilters.forEach((filter) => {
      this.addFilter(filter);
    });
  }

  private removeFormControl(filter: IFilter): void {
    this.form.removeControl(filter.name);
  }

  private reinitForm(): void {
    this.usedFilters = this.lastUsedFilters;
    this.unusedFilters = this.availableFilters.filter((u) => !this.lastUsedFilters.includes(u));
    if (Object.keys(this.lastFormValues).length === 0) {
      this.lastFormValues = { ...this.form.value };
    }
    this.form = new UntypedFormGroup({});
    Object.keys(this.lastFormValues).forEach((filter_name: string) => {
      const filter: IFilter = this.usedFilters.find((f) => f.name === filter_name) as IFilter;
      const value = this.lastFormValues[filter_name];
      switch (filter.type) {
        case FilterType.DATE_TIME:
          this.addFilter(filter, this.convertDateTimeRange(value) ?? []);
          break;
        case FilterType.DID:
          this.addFilter(filter, value.number ? value.number : value);
          break;
        case FilterType.INTERVAL:
          this.addFilter(filter, { from: Number(value.from), to: Number(value.to) });
          break;
        default:
          this.addFilter(filter, value);
      }
    });
  }

  private saveFormData(): void {
    this.lastFormValues = {};
    this.lastUsedFilters = [];
    this.lastFormValues = { ...this.form.value };
    this.lastUsedFilters = [...this.usedFilters];
  }
}
